import { Controller } from '@hotwired/stimulus'
import type { EChartsOption } from 'echarts'
import type { ECElementEvent } from 'echarts/core'
import SFetch from '@app/utils/sfetch'
import { SfLog } from '@app/adapters/logger'
import { readCSSVariable } from '@app/utils/domHelpers'
import { buildUrl } from '@app/application/behaviors/set-current-ledger'
import { EchartsHost } from '@app/shared/echarts/echarts-host'

interface TrendDataPoint {
  date: string
  value: number
  formattedDate: string
  formattedValue: string
  ledgerId: number | null
}

// Hexes are the sf-neutral Tailwind tokens, because a canvas colour cannot take
// a class.
const PAST_BAR_COLOR = '#d4d4d4' // sf-neutral-lighter-2
const PAST_BAR_HOVER_COLOR = '#171717' // sf-neutral-darker-1
const FALLBACK_BAR_COLOR = '#5b9bd5'
const DEFAULT_HEIGHT = 50
// Keeps small values visible next to large ones.
const BAR_MIN_HEIGHT = 4

const tooltipLine = (text: string, className: string): HTMLElement => {
  const line = document.createElement('div')
  line.className = className
  line.textContent = text
  return line
}

// Renders the small account history sparkline. The last bar is the current
// ledger and takes the firm's primary colour. Clicking a bar that maps to a
// ledger opens that period.
export default class extends Controller {
  static targets = ['chart', 'placeholder']
  static values = { url: String }

  declare readonly chartTarget: HTMLElement
  declare readonly placeholderTarget: HTMLElement
  declare readonly hasPlaceholderTarget: boolean
  declare urlValue: string

  private host: EchartsHost | null = null
  private dataPoints: TrendDataPoint[] = []

  connect(): void {
    // Fresh host per connection, captured by render() so an in-flight render
    // stays bound to the host disconnect() disposes, even across a reconnect.
    const host = new EchartsHost()
    this.host = host
    void this.render(host)
  }

  disconnect(): void {
    this.host?.dispose()
    this.host = null
  }

  private async render(host: EchartsHost): Promise<void> {
    if (!this.urlValue) return

    try {
      const data = await SFetch.json<{ dataPoints: TrendDataPoint[] }>(
        this.urlValue
      )
      if (host !== this.host) return
      this.dataPoints = data?.dataPoints || []

      // Clear the spinner even when there is nothing to plot, or an empty
      // response leaves it spinning.
      this.removePlaceholder()
      if (this.dataPoints.length === 0) return

      // mount() returns null when disconnect() ran during the fetch or the chunk
      // load.
      const chart = await host.mount(this.chartTarget, () =>
        this.sizeContainer()
      )
      if (!chart) return

      chart.setOption(this.buildOption())
      chart.on('click', (params: ECElementEvent) =>
        this.handleBarClick(params.dataIndex)
      )
    } catch (error) {
      this.removePlaceholder()
      SfLog.warn('Failed to render account trend chart', { error })
    }
  }

  private removePlaceholder(): void {
    if (this.hasPlaceholderTarget) this.placeholderTarget.remove()
  }

  private sizeContainer(): void {
    if (!this.chartTarget.clientHeight) {
      this.chartTarget.style.height = `${
        this.element.clientHeight || DEFAULT_HEIGHT
      }px`
    }
  }

  private buildOption(): EChartsOption {
    const primaryColor =
      readCSSVariable('--sf-color-primary-default').trim() || FALLBACK_BAR_COLOR
    const primaryHoverColor =
      readCSSVariable('--sf-color-primary-darker').trim() || primaryColor
    const lastIndex = this.dataPoints.length - 1

    return {
      grid: { left: 0, right: 0, top: 2, bottom: 0, containLabel: false },
      tooltip: {
        trigger: 'item',
        // The chart container has overflow:hidden, so render the tooltip on the
        // body to stop it being clipped at the chart edges.
        appendTo: 'body',
        padding: [4, 8],
        textStyle: { fontSize: 11 },
        extraCssText: 'text-align: center;',
        // The coordinates are relative to the chart container. Echarts moves the
        // body-appended tooltip to page coordinates itself, so adding the
        // container offset here would apply it twice.
        position: (point, _params, _dom, rect, size): [number, number] => {
          const [tooltipWidth, tooltipHeight] = size.contentSize
          const barCentreX = rect ? rect.x + rect.width / 2 : point[0]
          return [barCentreX - tooltipWidth / 2, -tooltipHeight - 8]
        },
        formatter: (params): string | HTMLElement[] => {
          const { dataIndex } = Array.isArray(params) ? params[0] : params
          const point = this.dataPoints[dataIndex]
          if (!point) return ''
          return [
            tooltipLine(
              point.formattedDate,
              'tw-pb-2 tw-text-sf-neutral-light-2'
            ),
            tooltipLine(
              point.formattedValue,
              'tw-font-bold tw-text-sf-neutral-darker-1'
            ),
          ]
        },
      },
      xAxis: {
        type: 'category',
        show: false,
        data: this.dataPoints.map((point) => point.formattedDate),
      },
      yAxis: { type: 'value', show: false },
      series: [
        {
          type: 'bar',
          barMinHeight: BAR_MIN_HEIGHT,
          cursor: 'pointer',
          data: this.dataPoints.map((point, index) => {
            // No bar at all, where barMinHeight would draw a stub.
            if (point.value === 0) return { value: null }
            const isCurrent = index === lastIndex
            return {
              value: Math.abs(point.value),
              itemStyle: { color: isCurrent ? primaryColor : PAST_BAR_COLOR },
              emphasis: {
                itemStyle: {
                  color: isCurrent ? primaryHoverColor : PAST_BAR_HOVER_COLOR,
                },
              },
            }
          }),
        },
      ],
    }
  }

  private handleBarClick(dataIndex: number): void {
    const point = this.dataPoints[dataIndex]
    if (!point?.ledgerId) return
    // buildUrl comes from an untyped JS module; String() pins it back to string.
    const url = String(buildUrl(window.location.href, String(point.ledgerId)))
    window.Turbolinks.visit(url)
  }
}
